import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '@services/adminService';
import {
  Search, Users, LogIn, KeyRound, UserX, UserCheck,
  Copy, X, MoreHorizontal,
} from 'lucide-react';
import toast from 'react-hot-toast';

const PLAN_COLORS = {
  free: 'bg-gray-700 text-gray-300',
  pro: 'bg-indigo-600/80 text-indigo-100',
  family: 'bg-purple-600/80 text-purple-100',
};

const ResetPasswordModal = ({ user, onClose, onConfirm, isPending, result }) => (
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
    <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-sm">
      <div className="flex items-center justify-between p-5 border-b border-gray-800">
        <h2 className="text-white font-semibold">Resetar Senha</h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-300">
          <X className="h-5 w-5" />
        </button>
      </div>

      {result ? (
        <div className="p-5 space-y-4">
          <p className="text-gray-400 text-sm">Senha temporária gerada para <span className="text-white">{user.name}</span>:</p>
          <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3">
            <span className="text-white font-mono text-lg flex-1 tracking-widest">{result.tempPassword}</span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(result.tempPassword);
                toast.success('Copiado!');
              }}
              className="text-gray-400 hover:text-white"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
          <p className="text-gray-500 text-xs">O usuário deverá alterar a senha no próximo login.</p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
          >
            Fechar
          </button>
        </div>
      ) : (
        <div className="p-5 space-y-4">
          <p className="text-gray-400 text-sm">
            Uma senha temporária será gerada para <span className="text-white">{user.name}</span> ({user.email}).
            O usuário precisará alterá-la no próximo login.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm text-gray-400 border border-gray-700 rounded-lg hover:border-gray-600"
            >
              Cancelar
            </button>
            <button
              onClick={onConfirm}
              disabled={isPending}
              className="flex-1 px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg disabled:opacity-50 font-medium"
            >
              {isPending ? 'Gerando...' : 'Gerar senha'}
            </button>
          </div>
        </div>
      )}
    </div>
  </div>
);

const ActionsMenu = ({ user, onImpersonate, onResetPassword, onToggleStatus, isPending }) => {
  const [open, setOpen] = useState(false);
  const isActive = user.status !== 'inactive';

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1.5 text-gray-500 hover:text-gray-300 hover:bg-gray-700 rounded"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-20 py-1">
            <button
              onClick={() => { onImpersonate(); setOpen(false); }}
              disabled={isPending}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
            >
              <LogIn className="h-3.5 w-3.5" />
              Impersonar
            </button>
            <button
              onClick={() => { onResetPassword(); setOpen(false); }}
              disabled={isPending}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
            >
              <KeyRound className="h-3.5 w-3.5" />
              Resetar senha
            </button>
            <div className="border-t border-gray-700 my-1" />
            <button
              onClick={() => { onToggleStatus(); setOpen(false); }}
              disabled={isPending}
              className={`flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-700 ${
                isActive ? 'text-red-400 hover:text-red-300' : 'text-green-400 hover:text-green-300'
              }`}
            >
              {isActive ? (
                <><UserX className="h-3.5 w-3.5" /> Desativar</>
              ) : (
                <><UserCheck className="h-3.5 w-3.5" /> Ativar</>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const AdminUsers = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [resetTarget, setResetTarget] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', { search, role: roleFilter }],
    queryFn: () =>
      adminService.getUsers({
        ...(search && { search }),
        ...(roleFilter && { role: roleFilter }),
      }),
  });

  const impersonateMutation = useMutation({
    mutationFn: ({ tenantId, userId }) => adminService.impersonateUser(tenantId, userId),
    onSuccess: (res) => {
      const { token, user } = res;
      window.open(`${window.location.origin}/app/dashboard?impersonate=${token}`, '_blank');
      toast.success(`Impersonando ${user.name}`);
    },
    onError: () => toast.error('Falha ao impersonar usuário'),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ tenantId, userId }) => adminService.resetUserPassword(tenantId, userId),
    onSuccess: (res) => {
      setResetResult(res);
    },
    onError: () => toast.error('Erro ao resetar senha'),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ tenantId, userId, status }) =>
      adminService.updateUserStatus(tenantId, userId, status),
    onSuccess: (_, { status }) => {
      toast.success(status === 'active' ? 'Usuário ativado' : 'Usuário desativado');
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: () => toast.error('Erro ao atualizar status do usuário'),
  });

  const users = data?.users || [];

  return (
    <div>
      {resetTarget && (
        <ResetPasswordModal
          user={resetTarget}
          onClose={() => { setResetTarget(null); setResetResult(null); }}
          onConfirm={() =>
            resetPasswordMutation.mutate({ tenantId: resetTarget.tenantId, userId: resetTarget.userId })
          }
          isPending={resetPasswordMutation.isPending}
          result={resetResult}
        />
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-white text-2xl font-bold">Usuários</h1>
          <p className="text-gray-400 text-sm">{data?.total ?? 0} no total</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            placeholder="Buscar por nome, email ou tenant..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-indigo-500 w-72"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-300 focus:outline-none focus:border-indigo-500"
        >
          <option value="">Todos os papéis</option>
          <option value="owner">Owner</option>
          <option value="admin">Admin</option>
          <option value="member">Member</option>
        </select>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
              <th className="py-3 px-4 text-left font-medium">Usuário</th>
              <th className="py-3 px-4 text-left font-medium">Tenant</th>
              <th className="py-3 px-4 text-left font-medium">Plano</th>
              <th className="py-3 px-4 text-left font-medium">Papel</th>
              <th className="py-3 px-4 text-left font-medium">Status</th>
              <th className="py-3 px-4 text-left font-medium">Último login</th>
              <th className="py-3 px-4 text-left font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="py-12 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500 mx-auto" />
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-12 text-center text-gray-500">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  Nenhum usuário encontrado
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr
                  key={`${u.tenantId}-${u.userId}`}
                  className={`border-b border-gray-800/50 hover:bg-gray-800/20 ${
                    u.status === 'inactive' ? 'opacity-50' : ''
                  }`}
                >
                  <td className="py-3 px-4">
                    <p className="text-white font-medium">{u.name}</p>
                    <p className="text-gray-500 text-xs">{u.email}</p>
                  </td>
                  <td className="py-3 px-4 text-gray-300">{u.tenantName}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PLAN_COLORS[u.tenantPlan] || 'bg-gray-700 text-gray-300'}`}>
                      {u.tenantPlan}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-400 capitalize">{u.role}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      u.status === 'inactive'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {u.status === 'inactive' ? 'inativo' : 'ativo'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-500 text-xs">
                    {u.lastLoginAt
                      ? new Date(u.lastLoginAt).toLocaleDateString('pt-BR')
                      : 'Nunca'}
                  </td>
                  <td className="py-3 px-4">
                    <ActionsMenu
                      user={u}
                      isPending={
                        impersonateMutation.isPending ||
                        resetPasswordMutation.isPending ||
                        toggleStatusMutation.isPending
                      }
                      onImpersonate={() =>
                        impersonateMutation.mutate({ tenantId: u.tenantId, userId: u.userId })
                      }
                      onResetPassword={() => setResetTarget(u)}
                      onToggleStatus={() =>
                        toggleStatusMutation.mutate({
                          tenantId: u.tenantId,
                          userId: u.userId,
                          status: u.status === 'inactive' ? 'active' : 'inactive',
                        })
                      }
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminUsers;
